import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { CARD_CLASS, OMS_COLORS } from '../../dashboard/omsDashTheme';
import { ADMIN_EYEBROW_CLASS } from '../adminTheme';
import { formatNumber } from '../../dashboard/utils/formatNumber';
import type { AdminPeakByState } from '../../../../services/dashboardService';
import { DOW_LABELS, buildPeakMatrix, type PeakMatrix } from '../utils/adminMetrics';
import './PulseHeatmap.css';

interface Props {
  /** Cells already narrowed to the drill scope by the page. */
  cells: Array<{ dow: number; hour: number; actions: number }>;
  /** State-faceted cells (portfolio-wide) for the comparison toggle. */
  byState: AdminPeakByState[];
  scopeCaption: string;
  loading: boolean;
}

const HOUR_LABELS = [0, 3, 6, 9, 12, 15, 18, 21];

function hourLabel(hour: number): string {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
}

/** Engraved intensity: paper → mint → petrol ink. */
function cellColor(value: number, max: number): string {
  if (value === 0 || max === 0) return 'rgba(11,59,46,0.05)';
  const t = value / max;
  if (t < 0.25) return '#CFE3D9';
  if (t < 0.5) return '#7BBFA3';
  if (t < 0.75) return '#339274';
  return '#14606B';
}

function MatrixGrid({ matrix, animate }: { matrix: PeakMatrix; animate: boolean }) {
  return (
    <div className="mt-2 overflow-x-auto pb-1">
      <div className="inline-block min-w-[560px]">
        <div className="ml-9 flex">
          {Array.from({ length: 24 }, (_, h) => (
            <span
              key={h}
              className="flex-1 text-center text-[8px] font-bold uppercase"
              style={{ color: OMS_COLORS.caption, minWidth: 21 }}
            >
              {HOUR_LABELS.includes(h) ? hourLabel(h) : ''}
            </span>
          ))}
        </div>
        {matrix.cells.map((row, d) => (
          <div key={d} className="flex items-center">
            <span className="w-9 pr-1.5 text-right text-[9px] font-bold uppercase" style={{ color: OMS_COLORS.caption }}>
              {DOW_LABELS[d]}
            </span>
            {row.map((value, h) => {
              const text = `${DOW_LABELS[d]} ${hourLabel(h)} — ${formatNumber(value)} app action${value === 1 ? '' : 's'}`;
              return (
                <span
                  key={h}
                  title={text}
                  aria-label={text}
                  className={animate ? 'pulse-cell m-[1.5px] flex-1 rounded-[3px]' : 'm-[1.5px] flex-1 rounded-[3px]'}
                  style={{
                    height: 18,
                    minWidth: 18,
                    backgroundColor: cellColor(value, matrix.max),
                    // The wake-up wave sweeps diagonally: delay grows with
                    // day + hour, whole sweep ≤ 600ms.
                    animationDelay: animate ? `${(d * 24 + h) * 2.6}ms` : undefined,
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * ★ Peak Activity Pulse — the Admin dashboard's signature. Hour-of-day ×
 * day-of-week matrix of app actions with computed callouts, plus a
 * per-state facet for regional comparison. Cells always carry exact
 * counts in tooltips; the intensity ladder only reinforces.
 */
export default function PulseHeatmap({ cells, byState, scopeCaption, loading }: Props) {
  const [facet, setFacet] = useState(false);
  const matrix = useMemo(() => buildPeakMatrix(cells), [cells]);

  const states = useMemo(() => {
    const names = [...new Set(byState.map((c) => c.state ?? 'Unmapped'))].sort();
    return names.map((name) => ({
      name,
      matrix: buildPeakMatrix(byState.filter((c) => (c.state ?? 'Unmapped') === name)),
    }));
  }, [byState]);

  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      aria-label={`Peak activity pulse: ${matrix.total} app actions; ${
        matrix.peak ? `peak ${DOW_LABELS[matrix.peak.dow - 1]} ${hourLabel(matrix.peak.hour)}` : 'no activity'
      }`}
      className={`${CARD_CLASS} p-4`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className={ADMIN_EYEBROW_CLASS}>Peak activity pulse · {scopeCaption}</p>
          <p className="mt-0.5 text-[11px] font-semibold" style={{ color: OMS_COLORS.caption }}>
            {matrix.total === 0
              ? 'No app actions in this window.'
              : matrix.peak && matrix.quietestDay
                ? `Peak: ${DOW_LABELS[matrix.peak.dow - 1]}s ${hourLabel(matrix.peak.hour)}–${hourLabel((matrix.peak.hour + 1) % 24)} (${formatNumber(matrix.peak.actions)} actions) · Quietest: ${DOW_LABELS[matrix.quietestDay.dow - 1]}s · ${formatNumber(matrix.total)} actions total`
                : `${formatNumber(matrix.total)} actions total`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide" style={{ color: OMS_COLORS.caption }}>
            Less
            {['rgba(11,59,46,0.05)', '#CFE3D9', '#7BBFA3', '#339274', '#14606B'].map((color) => (
              <span key={color} className="h-2.5 w-2.5 rounded-[3px]" style={{ backgroundColor: color }} />
            ))}
            More
          </span>
          <button
            type="button"
            aria-pressed={facet}
            onClick={() => setFacet((v) => !v)}
            className={`rounded-lg border px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition ${
              facet
                ? 'border-[#14606B] bg-[rgba(20,96,107,0.12)] text-[#14606B]'
                : 'border-[rgba(11,59,46,0.24)] bg-[#fcfaf3] text-[#5A6275] hover:text-[#14606B]'
            }`}
          >
            Compare states
          </button>
        </div>
      </div>

      {loading && matrix.total === 0 ? (
        <div className="mt-3 h-44 animate-pulse rounded-xl bg-[rgba(11,59,46,0.06)]" />
      ) : !facet ? (
        <MatrixGrid matrix={matrix} animate />
      ) : (
        <div className="mt-2 grid gap-4 lg:grid-cols-2">
          {states.map((state) => (
            <div key={state.name}>
              <p className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: OMS_COLORS.tealDark }}>
                {state.name}
                <span className="ml-2 font-semibold normal-case tracking-normal" style={{ color: OMS_COLORS.caption }}>
                  {state.matrix.peak
                    ? `peak ${DOW_LABELS[state.matrix.peak.dow - 1]} ${hourLabel(state.matrix.peak.hour)} · ${formatNumber(state.matrix.total)} actions`
                    : 'no activity'}
                </span>
              </p>
              <MatrixGrid matrix={state.matrix} animate={false} />
            </div>
          ))}
        </div>
      )}
    </motion.section>
  );
}
